'use client';

/**
 * SysObeyaModuleTableCard — sortable module-metrics table sliced from
 * `response.data[]` of the system_obeya_view composite call.
 *
 * Typography from `useTypographyBundle('canvas_dashboard')`:
 *   header   = card title (20px font-semibold)
 *   subtitle = card subtitle (14px muted)
 *   body     = cell content (14px) — canvas-readable, not rail-density
 *   name     = module name primary accent (14px font-medium text-primary)
 *   metadata = column-header label, footnotes (12px muted)
 *   amount   = numeric cell values where primary highlight matters
 *
 * Container chrome (card padding, table cell padding, column-header
 * styling, hover row tint) lives here as render concerns per the
 * mode7 doctrine. Chrome strings come from `response.labels`.
 *
 * @see klyde_edge/ui_config/composition/cards/system_obeya.py
 * @see klyde_edge/ui_config/labels/system_obeya.py — mod_sum_col_*
 */

import { useState, useMemo } from 'react';
import { Layers, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { type TypographyBundleSlots } from '@/lib/design/typography';
import { useCardTypography } from '@/lib/design/card-chrome';
import { useCardExport } from '@/lib/design/card-export';
import { useSystemObeyaView, type SystemObeyaLabels } from './use-system-obeya-view';

interface Props {
  view_name?: string;
}

type SortDir = 'asc' | 'desc';
type Align = 'left' | 'right';

interface ColumnDef {
  key: string;
  label_key: string;
  align: Align;
}

const COLUMNS: ColumnDef[] = [
  { key: 'package',                label_key: 'mod_sum_col_module',          align: 'left'  },
  { key: 'files_count',            label_key: 'mod_sum_col_files',           align: 'right' },
  { key: 'sloc',                   label_key: 'mod_sum_col_sloc',            align: 'right' },
  { key: 'commits',                label_key: 'mod_sum_col_commits',         align: 'right' },
  { key: 'function_points',        label_key: 'mod_sum_col_function_points', align: 'right' },
  { key: 'story_count',            label_key: 'mod_sum_col_stories',         align: 'right' },
  { key: 'declarative_percentage', label_key: 'mod_sum_col_declarative',     align: 'right' },
  { key: 'quality_score',          label_key: 'mod_sum_col_quality',         align: 'right' },
  { key: 'test_coverage',          label_key: 'mod_sum_col_test_coverage',   align: 'right' },
];

export function SysObeyaModuleTableCard({ view_name = 'module_summary' }: Props) {
  const { data, isLoading, queryKey } = useSystemObeyaView(view_name);
  const T = useCardTypography();
  const L: SystemObeyaLabels = data?.labels ?? {};

  const [sortKey, setSortKey] = useState<string>('sloc');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rows = useMemo(() => {
    if (!data?.data) return [];
    return [...data.data].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av ?? '');
      const bs = String(bv ?? '');
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
  }, [data, sortKey, sortDir]);

  // Card export — markdown table of visible (sorted) rows.
  const title = L.mod_sum_table_title ?? '';
  useCardExport(useMemo(() => {
    const headers = COLUMNS.map((c) => L[c.label_key] ?? c.key);
    const headerRow = `| ${headers.join(' | ')} |`;
    const divRow = `| ${headers.map(() => '---').join(' | ')} |`;
    const dataRows = rows.map((row) =>
      `| ${COLUMNS.map((c) => formatCellForExport(c, row, L)).join(' | ')} |`
    );
    const markdown = [headerRow, divRow, ...dataRows].join('\n');
    // Scene json mirrors what the user SAW in each cell — the
    // formatter's OUTPUT (`"14,953"`, `"85%"`), not its input
    // (`14953`, `85`). The raw underlying numbers travel in the
    // capture envelope's `payloads` section, so the pair supports
    // `format(payload.sloc, labels) == scene.row.sloc` as the
    // render-correctness check. Scene = screen truth.
    const visibleRows = rows.map((r) =>
      Object.fromEntries(COLUMNS.map((c) => [c.key, formatCellForExport(c, r, L)]))
    );
    return {
      label: title,
      text: rows.map((r) => COLUMNS.map((c) => `${L[c.label_key] ?? c.key}=${formatCellForExport(c, r, L)}`).join(', ')).join('\n'),
      markdown,
      json: { columns: COLUMNS.map((c) => c.key), rows: visibleRows },
      content_key: 'sys_obeya_module_table',
      surface: 'canvas',
      state: { sort_key: sortKey, sort_dir: sortDir },
      // Status = paintable content, not the query flag — `data`
      // presence is what determines whether rows are visible.
      // See feedback on the synopsis card's isLoading drift.
      status: (data ? 'loaded' : 'loading') as 'loading' | 'loaded',
      // Capture-envelope derivation source — the BFF query this
      // table consumes. Surfaced directly from the hook so the
      // card doesn't reconstruct the key (no drift risk).
      query_key: queryKey,
    };
  }, [rows, L, title, sortKey, sortDir, data, isLoading, queryKey]));

  if (isLoading || !data) {
    return (
      <CardShell>
        <div className="p-6">
          <div className={T.body}>{L.loading_message ?? ''}</div>
        </div>
      </CardShell>
    );
  }

  return (
    <CardShell>
      <div className="p-6 border-b border-border/50">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-primary" />
          <h2 className={T.header}>{L.mod_sum_table_title ?? ''}</h2>
        </div>
        <p className={cn(T.subtitle, 'mt-1')}>{L.mod_sum_table_subtitle ?? ''}</p>
      </div>
      <div className="overflow-auto flex-1">
        <table className="w-full">
          <thead className="sticky top-0 bg-card">
            <tr className="border-b border-border/50">
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  onClick={() => {
                    if (sortKey === col.key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                    else { setSortKey(col.key); setSortDir('desc'); }
                  }}
                  className={cn(
                    T.metadata,
                    'uppercase tracking-wider font-semibold px-3 py-3 cursor-pointer hover:text-primary select-none',
                    col.align === 'right' ? 'text-right' : 'text-left',
                  )}
                >
                  <span className="inline-flex items-center gap-1">
                    {L[col.label_key] ?? ''}
                    {sortKey === col.key && (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={(row.package as string) ?? `row-${idx}`} className="border-b border-border/40 hover:bg-muted/30 transition-colors">
                {COLUMNS.map((col) => (
                  <Cell key={col.key} col={col} row={row} T={T} L={L} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {L.mod_sum_disclaimer && (
        <div className={cn(T.subtitle, 'px-6 py-4 border-t border-border/50 text-center')}>
          {L.mod_sum_disclaimer}
        </div>
      )}
    </CardShell>
  );
}

// ─── Cell renderer ──────────────────────────────────────────────────

function Cell({
  col,
  row,
  T,
  L,
}: {
  col: ColumnDef;
  row: Record<string, unknown>;
  T: TypographyBundleSlots;
  L: SystemObeyaLabels;
}) {
  const dash = L.dash_placeholder ?? '—';

  // First column (module identity) — name uses T.name (text-primary
  // by bundle), description below sits at metadata-scale.
  if (col.key === 'package') {
    const moduleName = (row.display_name as string) || (row.package as string) || '';
    const description = (row.description as string) || '';
    return (
      <td className={cn('px-3 py-2.5', col.align === 'right' ? 'text-right' : 'text-left')}>
        <div className={cn(T.name, 'font-mono')}>{moduleName || dash}</div>
        {description && (
          <div className={cn(T.metadata, 'truncate max-w-[40ch]')} title={description}>
            {description}
          </div>
        )}
      </td>
    );
  }

  // SLOC gets the primary accent (the headline number in legacy).
  if (col.key === 'sloc') {
    const value = row[col.key];
    return (
      <td className={cn('px-3 py-2.5', col.align === 'right' ? 'text-right' : 'text-left')}>
        <span className={cn(T.body, 'font-mono font-medium text-primary tabular-nums')}>
          {value == null ? dash : fmt(value)}
        </span>
      </td>
    );
  }

  // Percent-shaped fields render with a trailing %.
  const value = row[col.key];
  const isPercent = col.key.endsWith('_percentage') || col.key.endsWith('_pct') || col.key === 'test_coverage';
  const rendered = value == null ? dash : isPercent ? `${fmt(value)}%` : fmt(value);

  return (
    <td className={cn('px-3 py-2.5', col.align === 'right' ? 'text-right' : 'text-left')}>
      <span className={cn(T.body, 'font-mono tabular-nums')}>{rendered}</span>
    </td>
  );
}

// ─── Shell + numeric format helper ──────────────────────────────────

function CardShell({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl border border-border bg-card shadow-sm h-full overflow-hidden flex flex-col">{children}</div>;
}
function fmt(n: unknown): string {
  if (n == null || typeof n !== 'number') return '—';
  return n.toLocaleString();
}

// Cell formatter used by the card export to compose a markdown
// table row. Same identity-vs-numeric branching as the on-screen
// Cell renderer, simplified to a single string per cell.
function formatCellForExport(
  col: ColumnDef,
  row: Record<string, unknown>,
  L: SystemObeyaLabels,
): string {
  const dash = L.dash_placeholder ?? '—';
  if (col.key === 'package') {
    return (row.display_name as string) || (row.package as string) || dash;
  }
  const value = row[col.key];
  if (value == null) return dash;
  const isPercent = col.key.endsWith('_percentage') || col.key.endsWith('_pct') || col.key === 'test_coverage';
  return isPercent ? `${fmt(value)}%` : fmt(value);
}
